import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash, SignOut, Phone, EnvelopeSimple, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.svg";

export default function AdminDashboard() {
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('leads');
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');

  const fetchData = useCallback(async () => {
    try {
      const [contactsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setContacts(contactsRes.data);
      setLeads(leadsRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_name');
        navigate('/admin');
      }
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [token, navigate, fetchData]);

  const handleDelete = async (id, type = 'contacts') => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (type === 'leads') setLeads(prev => prev.filter(c => c.id !== id));
      else setContacts(prev => prev.filter(c => c.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');
    navigate('/admin');
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CSF" className="h-9 w-9 object-contain" />
            <span className="font-display font-bold text-white text-sm tracking-tight">
              Admin Dashboard
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#A3A3A3] hidden sm:block">
              {localStorage.getItem('admin_name')}
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 text-[#A3A3A3] hover:text-white transition-colors text-sm"
            >
              <SignOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-6 mb-8">
          <button onClick={() => setTab('leads')} className={`font-display font-bold text-lg transition-colors ${tab === 'leads' ? 'text-white' : 'text-[#666] hover:text-white'}`}>
            Leads ({leads.length})
          </button>
          <button onClick={() => setTab('contacts')} className={`font-display font-bold text-lg transition-colors ${tab === 'contacts' ? 'text-white' : 'text-[#666] hover:text-white'}`}>
            Contacts ({contacts.length})
          </button>
          <div className="flex-1" />
          <a href="/" data-testid="admin-back-to-site" className="text-xs text-[#A3A3A3] hover:text-white transition-colors uppercase tracking-wider">View Site</a>
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#A3A3A3]">Loading...</div>
        ) : (
          <div className="space-y-4">
            {(tab === 'leads' ? leads : contacts).length === 0 ? (
              <div className="text-center py-20 border border-white/10 bg-[#141414]"><p className="text-[#A3A3A3] text-sm">No {tab} yet</p></div>
            ) : (
              (tab === 'leads' ? leads : contacts).map(c => (
                <div key={c.id} data-testid={`inquiry-card-${c.id}`} className="bg-[#141414] border border-white/10 p-6 hover:border-[#E31937]/30 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-display font-semibold text-white text-lg truncate">{c.name}</h3>
                        {c.service && <span className="text-[10px] bg-[#E31937]/20 text-[#E31937] px-2 py-0.5 uppercase font-bold tracking-wider">{c.service}</span>}
                        {c.sms_consent && <span className="text-[10px] bg-green-900/30 text-green-400 px-2 py-0.5 uppercase font-bold">SMS OK</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                        <span className="flex items-center gap-1 text-xs text-[#A3A3A3]"><EnvelopeSimple size={12} className="text-[#E31937]" />{c.email}</span>
                        {c.phone && <span className="flex items-center gap-1 text-xs text-[#A3A3A3]"><Phone size={12} className="text-[#E31937]" />{c.phone}</span>}
                        <span className="flex items-center gap-1 text-xs text-[#666]"><Clock size={12} />{formatDate(c.created_at)}</span>
                        {c.zip_code && <span className="text-xs text-[#666]">ZIP: {c.zip_code}</span>}
                        {c.timeline && <span className="text-xs text-[#666]">{c.timeline}</span>}
                      </div>
                    </div>
                    <button onClick={() => handleDelete(c.id, tab)} data-testid={`delete-inquiry-${c.id}`} className="text-[#666] hover:text-[#E31937] transition-colors p-2 shrink-0"><Trash size={18} /></button>
                  </div>
                  {c.message && <div className="mt-3 pt-3 border-t border-white/5"><p className="text-sm text-[#A3A3A3] whitespace-pre-wrap">{c.message}</p></div>}
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
